import React, { useEffect, useState } from "react";
import {
  Modal as AntModal,
  Button,
  Form,
  Image,
  Input,
  message,
  Space,
} from "antd";
import styled from "styled-components";
import { IconCheckLight, IconUploadImage } from "../../../../assets/icons";
import api from "../../../../api/api";
import { CloseCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const Modal = styled(AntModal)`
  .ant-modal-header {
    margin-bottom: 16px;
  }

  .ant-modal-footer {
    display: none;
  }

  .ant-form {
    .ant-form-item:last-child {
      margin-bottom: 0;
    }

    #file-input {
      display: none;
    }
  }

  .ant-space {
    width: 100%;

    .ant-space-item {
      flex: 0.5;
    }

    .ant-btn {
      width: 100%;
      height: 48px;
    }
  }
`;

const UploadWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;

  label {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-image,
  img {
    height: 210px !important;
    width: auto !important;
  }
`;

const LightText = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #868686;
  line-height: 1.5;
`;

const GrayText = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #b9b9b9;
  white-space: pre-wrap;
  text-align: center;
`;

const Box = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1; /* makes it perfectly square */
  max-width: 310px; /* optional, controls preview size */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  background-color: ${(props) => props.theme.backgroundColor};
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  .anticon {
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 24px;
    color: #868686;
    z-index: 10;
  }

  img,
  .ant-image-img {
    width: 100%;
    height: 100%;
    object-fit: cover; /* makes image fill square */
  }
`;


const SeoInfoModal = ({ open, data, handleClose, successCallback }) => {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  const {t} = useTranslation();

  useEffect(() => {
    if (!open) {
      setFile(null);
      setImageUrl(null);
      setLoading(false);
    } else {
      setImageUrl(data?.image);
    }

    // eslint-disable-next-line
  }, [open]);

  const onFinish = ({ seo_title, seo_description }) => {
    if (!loading) {
      if (!file && !imageUrl) {
        return message.error(t('seo_image_require'))
      }
      setLoading(true);
      let payload = {
        seo_title,
        seo_description,
      };
      if (file) {
        api
          .uploadMedia({
            file,
            file_path: file.type,
          })
          .then(({ data: { url } }) => {
            payload.seo_image_url = url;
            doPost(payload);
          })
          .catch((error) => {
            setLoading(false);
            message.error(error);
          });
      } else {
        payload.seo_image_url = data?.image;
        doPost(payload);
      }
    }
  };

  const doPost = (payload) => {
    api
      .updateStoreSeo(payload)
      .then(() => {
        setLoading(false);
        message.success(t('saved_seo_image'));
        successCallback(payload);
        handleClose();
      })
      .catch((error) => {
        setLoading(false);
        message.error(error);
      });
  };

  const onCancel = () => {
    !loading && handleClose();
  };


const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpg", "image/jpeg"];
    if (!validTypes.includes(file.type)) {
      message.error(t('limit_image_format'));
      e.target.value = "";
      return;
    }

    const img = new window.Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      if (img.width !== img.height) {
        message.error(t('seo_upload_ratio'));
        e.target.value = "";
        return;
      }
      setFile(file);
    };
  };


  const handleRemoveClicked = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFile(null);
    setImageUrl(null);
  };

  const UploadImage = () => (
    <UploadWrapper>
      <label htmlFor="file-input">
        <Box>
          {file || imageUrl ? (
            <Image
              src={file ? URL.createObjectURL(file) : imageUrl}
              preview={false}
            />
          ) : (
            <>
              <IconUploadImage />
              <GrayText>{t('upload_image_or_drag_file')}</GrayText>
            </>
          )}
          {(file || imageUrl) && (
            <CloseCircleFilled onClick={handleRemoveClicked} />
          )}
        </Box>
      </label>
     <input
        id="file-input"
        type="file"
        accept=".png,.jpg,.jpeg"
        disabled={file || imageUrl}
        hidden
        onChange={handleFileChange}
      />
    <LightText style={{ textAlign: "center" }}>
   {t('seo_red_image')}
</LightText>
    </UploadWrapper>
  );

  return (
    <Modal
      centered
      open={open}
      title={t('edit_seo')}
      onCancel={onCancel}
      footer={false}
    >
      <Form initialValues={{ ...data }} onFinish={onFinish}>
        <Form.Item>
          <UploadImage />
        </Form.Item>
        <Form.Item name={"seo_title"} rules={[{ required: true, message: "" }]}>
          <Input placeholder={t('seo_name')} maxLength={50} showCount />
        </Form.Item>
        <Form.Item
          name={"seo_description"}
          rules={[{ required: true, message: "" }]}
        >
          <Input.TextArea
            placeholder={t('seo_description')}
            rows={4}
            maxLength={300}
            showCount
          />
        </Form.Item>
        <Form.Item>
          <Space size={"middle"}>
            <Button type="default" onClick={onCancel}>
              {t('cancel')}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<IconCheckLight />}
              loading={loading}
            >
              {t('save')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SeoInfoModal;
